import { Button, ChakraAsyncSelect, FormControl, FormLabel, Stack, useDisclosure } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { BaseFilterButton } from 'components/base-table/filters/base-filter-button';
import { usePageParams } from 'hooks/use-page-params';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

type TInputs = {
  brandId?: {
    value: number;
    label: string;
  };
};

export const BrandFilterButton = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { setPageParams, getPageParams } = usePageParams();
  const { brandId } = getPageParams();
  const [selectedBrand, setSelectedBrand] = useState<{ value: number; label: string } | null>(null);

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    // watch,
    formState: { isSubmitting },
  } = useForm<TInputs>();

  // Watch the brandId field value
  // const watchedBrandId = watch('brandId');

  // Dynamic search function that calls the API with search term
  // When inputValue is empty, it loads default options
  const loadBrandOptions = useCallback(async (inputValue: string) => {
    try {
      const { data } = await http.get('ums/v1/crm/management/brands', {
        params: {
          page: 1,
          limit: inputValue ? 50 : 20, // More results when searching, fewer for default
          ...(inputValue && { name: inputValue }), // Only add name param if there's input
        },
      });

      return (data?.data || []).map((brand: any) => ({
        value: brand.id,
        label: brand.name,
      }));
    } catch (error) {
      console.error('Error loading brands:', error);
      return [];
    }
  }, []);

  const onSubmit = (data: TInputs) => {
    const currentParams = getPageParams();
    const newParams = { ...currentParams };

    if (data.brandId?.value) {
      newParams.brandId = data.brandId.value;
    } else {
      // If no brand is selected, remove the parameter
      delete newParams.brandId;
    }

    setPageParams(newParams);
    onClose();
  };

  const onClear = () => {
    const currentParams = getPageParams();
    const newParams = { ...currentParams };
    delete newParams.brandId;
    setPageParams(newParams);
    reset();
    setSelectedBrand(null);
    onClose();
  };

  // Load current brand details when filter opens
  useEffect(() => {
    if (isOpen && brandId) {
      // Fetch the specific brand to get its name
      const fetchCurrentBrand = async () => {
        try {
          const { data } = await http.get('ums/v1/crm/management/brands', {
            params: {
              page: 1,
              limit: 1,
              id: brandId,
            },
          });

          const brand = data?.data?.[0];
          if (brand) {
            const brandOption = {
              value: brand.id,
              label: brand.name,
            };
            setValue('brandId', brandOption);
            setSelectedBrand(brandOption);
          }
        } catch (error) {
          console.error('Error loading current brand:', error);
          // Fallback
          const fallbackOption = {
            value: Number(brandId),
            label: `Brand ${brandId}`,
          };
          setValue('brandId', fallbackOption);
          setSelectedBrand(fallbackOption);
        }
      };

      fetchCurrentBrand();
    } else if (isOpen && !brandId) {
      reset();
      setSelectedBrand(null);
    }
  }, [isOpen, brandId, setValue, reset]);

  const hasFilters = !!brandId;

  const handleClose = () => {
    onClose();
  };

  // Prevent form submission from closing the modal
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(onSubmit)(e);
  };

  // Custom onChange handler to prevent auto-close
  const handleBrandChange = useCallback(
    (selectedOption: any) => {
      setSelectedBrand(selectedOption);
      setValue('brandId', selectedOption);
      // Do NOT close the dropdown here
    },
    [setValue]
  );

  return (
    <BaseFilterButton
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      isActive={hasFilters}
      formComponent={
        <Stack as='form' spacing='lg' p='md' pt='lg' onSubmit={handleFormSubmit} onClick={(e) => e.stopPropagation()}>
          <FormControl>
            <FormLabel>Brand</FormLabel>
            <Controller
              name='brandId'
              control={control}
              render={({ field }) => (
                <ChakraAsyncSelect
                  {...field}
                  value={selectedBrand}
                  loadOptions={loadBrandOptions}
                  defaultOptions={true} // Load default options on mount
                  placeholder='Search and select a brand...'
                  isClearable
                  cacheOptions={false} // Disable cache to allow fresh searches
                  loadingMessage={() => 'Searching brands...'}
                  noOptionsMessage={({ inputValue }) =>
                    inputValue ? `No brands found for "${inputValue}"` : 'Type to search brands'
                  }
                  // Critical: Prevent dropdown from auto-closing
                  closeMenuOnSelect={false}
                  blurInputOnSelect={false}
                  // Custom change handler
                  onChange={handleBrandChange}
                  // Additional properties to prevent auto-close
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  // Ensure menu stays open
                  menuIsOpen={undefined} // Let the component control this
                />
              )}
            />
          </FormControl>

          <Stack direction='row' justify='flex-end' spacing={2}>
            <Button variant='outline' colorScheme='red' onClick={onClear}>
              Clear
            </Button>
            <Button variant='outline' colorScheme='gray' onClick={handleClose}>
              Cancel
            </Button>
            <Button variant='solid' colorScheme='blue' type='submit' isLoading={isSubmitting}>
              Filter
            </Button>
          </Stack>
        </Stack>
      }
    />
  );
};
